import { documentElement } from "@acng/frontend-bounty";
import { ReferenceError, TypeError, isObject } from "@acng/frontend-bounty/types";
import { createMap, existsOnMap, forEachOfMap, putOnMap } from "@acng/frontend-bounty/map";
import { createWeakMap, getFromWeakMap, setInWeakMap } from "@acng/frontend-bounty/weakmap";
import { HISTORY_GET_STATE, HISTORY_PUSH_STATE, HISTORY_REPLACE_STATE } from "@acng/frontend-bounty/history";
import { createGlobalContext } from "../model/context/global.js";
import { CTX_PROVIDE, CTX_VALUE } from "../config/context.js";
const MODULE = "relativity/service/history";
/**
 * @module
 * @example
 * ```javascript
 * export function addHistoryListeners() {
 *   onWindow(EVENT_HASHCHANGE, onHashchange);
 *   onWindow(EVENT_POPSTATE, onPopstate);
 * }
 * ```
 */
/**
 * Changes the url in the browser address bar by replacing the current browsing history entry.
 *
 * @param {string | URL} url
 * @returns {void}
 */
export function replaceUrl(url) {
    DEBUG: console.info(`${MODULE} replace url`, { url, previous: location.href });
    HISTORY_REPLACE_STATE(HISTORY_GET_STATE(), url);
}
/**
 * @returns {void}
 */
export function pushHistory() {
    const state = getState();
    DEBUG: console.info(`${MODULE} push history`, { state });
    HISTORY_PUSH_STATE(state);
}
/**
 * @type {Map<string, Context<unknown>>}
 */
const entries = createMap();
/**
 * @type {WeakMap<Context<unknown>, (provider: Element | null, value: ContextValue<any>) => any>}
 */
const originalProvideFns = createWeakMap();
/**
 * @template T
 * @param {string} historyKey
 * @param {ContextValue<T>} initialValue
 * @param {boolean} [isPushing]
 * @returns {Context<T>}
 */
export function createHistoryEntry(historyKey, initialValue, isPushing) {
    DEBUG: if (existsOnMap(entries, historyKey)) {
        throw ReferenceError();
    }
    const context = createGlobalContext(initialValue);
    const originalProvideFn = context[CTX_PROVIDE];
    context[CTX_PROVIDE] = (provider, value) => {
        const state = getState();
        DEBUG: console.debug(`${MODULE} ${isPushing ? "push" : "replace"} ${historyKey}`, { value });
        (isPushing ? HISTORY_PUSH_STATE : HISTORY_REPLACE_STATE)({ ...state, [historyKey]: value });
        return originalProvideFn(provider, value);
    };
    setInWeakMap(originalProvideFns, context, originalProvideFn);
    putOnMap(entries, historyKey, context);
    HISTORY_REPLACE_STATE({ ...getState(), [historyKey]: initialValue });
    return context;
}
/**
 * Put the value of each created history entry into the current browser history state.
 *
 * @param {HashChangeEvent} _ev
 */
export const onHashChange = (_ev) => {
    DEBUG: console.info(`${MODULE} hash change`, { hash: location.hash });
    /**
     * @type {Record<string, unknown>}
     */
    const data = { ...getState() };
    forEachOfMap(entries, (v, k) => (data[k] = v[CTX_VALUE](documentElement)));
    HISTORY_REPLACE_STATE(data);
};
/**
 * Provide a value for each created history entry.
 *
 * @param {PopStateEvent} ev
 * The provided values are gathered from the ev.state property,
 * which is expected to be a {@link Record<string, unknown>}
 * with an entry for each created history entry.
 */
export const onPopState = ({ state }) => {
    if (!state) {
        DEBUG: console.warn(`${MODULE} POPSTATE WITHOUT STATE - THIS IS NOT SUPPOSED TO HAPPEN - onHashChange HAS TO FOLLOW TO FIX THIS!`);
        throw TypeError(MODULE);
    }
    DEBUG: console.assert(isObject(state), "PopStateEvent.state is not an object", { state });
    DEBUG: console.info(`${MODULE} pop state`, { state });
    forEachOfMap(entries, (v, k) => {
        const currentValue = v[CTX_VALUE](documentElement);
        if (currentValue == state[k]) {
            DEBUG: console.debug(`${MODULE} skip`, { k, v: state[k], currentValue });
            return;
        }
        DEBUG: console.debug(`${MODULE} original provide`, { k, v: state[k] });
        const originalProvideFn = getFromWeakMap(originalProvideFns, v);
        DEBUG: if (!originalProvideFn) {
            throw ReferenceError(`${MODULE} original provide function not found`);
        }
        originalProvideFn(null, state[k]);
    });
};
const getState = () => {
    const state = HISTORY_GET_STATE();
    DEBUG: if (!isObject(state) && state !== null) {
        console.error("history state has to be an object (or null)", { state });
        throw TypeError();
    }
    return state;
};
/**
 * @template T
 * @typedef {import("../model/context/interface").Context<T>} Context<T>
 */
/**
 * @template T
 * @typedef {import("../model/context/interface").ContextValue<T>} ContextValue<T>
 */
